/* eslint-disable security/detect-non-literal-fs-filename */
import { call, put, fork, select } from '@redux-saga/core/effects'
import { utils, writeFileXLSX } from 'xlsx'
import dayjs from 'dayjs'
import { Action } from 'infra/types'
import { NotificationType } from 'pages/Notification/Notification.types'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import {
  getOrderSummaryApi,
  getOrderSummaryItemsApi,
  getOrderSummaryByGroupIdApi
} from 'services/Api/StafflinkApi'
import {
  getOverallOrderOuterSummaryApi,
  getOverallOrderSummaryGroupApi
} from 'services/Api/StaffLinkApiFolder/api'
import { IAppState } from 'infra/AppState'
import { getMaxColumnWidths } from 'state/statements/statements.helpers'
import {
  DEFAULT_ORDER_SUMMARY_LIMIT,
  INITIAL_OUTER_DATA
} from 'state/orderSummary/orderSummary.state'
import { formatDateToString } from 'utils/time'
import { ITagsState } from 'state/tags/tags.types'
import { API_RESPONSE_TYPE, IApiResponse } from '../../constants'
import {
  setLoaderAction,
  setOrderSummary,
  setOrderSummaryListItems,
  setSummaryActiveFilter,
  getOrderGroupSummaryActionSuccess,
  setOrderSummaryModalsAction,
  getOverallOrderGroupSummarySuccessAction
} from './orderSummary.actions'
import {
  formatOrderGroupSummaryRowData,
  formatOrderSummaryListRowData,
  formatOrderSummaryRowData,
  formatOrderGroupSummaryRowDataDownload,
  formatOrderSummaryListRowDataDownload,
  getFormattedOverallOrderGroupSummary,
  getFormattedOuterOrderGroupSummary,
  getExcelHeading,
  formatDataForExcel,
  getOrderColumnWidths,
  getFormattedOverallOrderGroupSummaryV2
} from './orderSummary.helpers'
import {
  IFetchOrderSummaryActionPayload,
  IFetchOrderGroupSummaryActionPayload,
  IFetchOrderGroupSummaryDownlaodActionPayload,
  IOrderSummaryState
} from './orderSummary.types'

function* fetchOrderSummary(payload: IFetchOrderSummaryActionPayload) {
  const orderSummaryResponse: IApiResponse = yield call(getOverallOrderOuterSummaryApi, {
    from_date: payload.startTime,
    to_date: payload.endTime
  })
  if (orderSummaryResponse.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch summary. Please try again after some time.', {
      cause: 'customError'
    })
  }
  if (orderSummaryResponse?.data?.order_summary_stats?.length) {
    yield put(
      setOrderSummary(
        getFormattedOuterOrderGroupSummary(orderSummaryResponse?.data?.order_summary_stats[0])
      )
    )
  } else {
    yield put(setOrderSummary(INITIAL_OUTER_DATA))
  }
  yield put(setLoaderAction({ id: 'orderSummary', value: false }))
}

function* fetchOverallList(payload: IFetchOrderSummaryActionPayload) {
  const response: IApiResponse = yield call(getOrderSummaryItemsApi, {
    from_date: payload.startTime,
    to_date: payload.endTime
  })
  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: 'Could not fetch summary. Please try again after some time.'
      })
    )
    throw new Error('Could not fetch summary. Please try again after some time.', {
      cause: 'customError'
    })
  }
  if (response?.data?.order_summary?.length) {
    const formattedSummary = formatOrderSummaryListRowData(response.data.order_summary)
    yield put(setOrderSummaryListItems(formattedSummary))
  }
  yield put(setLoaderAction({ id: 'allOrderListLoader', value: false }))
}

export function* getOrderSummaryEffect(action: Action<IFetchOrderSummaryActionPayload>) {
  yield fork(fetchOrderSummary, action.payload)
  yield fork(fetchOverallList, action.payload)
  yield put(
    setSummaryActiveFilter({
      startTime: action.payload.startTime,
      endTime: action.payload.endTime,
      filterType: action.payload.filterType
    })
  )
}

export function* getOrderGroupSummaryEffect(action: Action<IFetchOrderGroupSummaryActionPayload>) {
  const response: IApiResponse = yield call(getOrderSummaryByGroupIdApi, {
    from_date: action.payload.startTime,
    to_date: action.payload.endTime,
    group_id: action.payload.groupId
  })
  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: 'Could not fetch order group summary. Please try again after some time.'
      })
    )
    throw new Error('Could not fetch order group summary. Please try again after some time.', {
      cause: 'customError'
    })
  }
  if (response?.data?.order_summary?.length) {
    const formattedSummary = formatOrderGroupSummaryRowData(response.data.order_summary[0])
    yield put(getOrderGroupSummaryActionSuccess(formattedSummary))
  }
  yield put(
    setSummaryActiveFilter({
      startTime: action.payload.startTime,
      endTime: action.payload.endTime,
      filterType: action.payload.filterType
    })
  )
  yield put(setLoaderAction({ id: 'groupSummary', value: false }))
}

export function* downloadOrderSummarySagaEffect(
  action: Action<IFetchOrderGroupSummaryDownlaodActionPayload>
) {
  const { startTime, endTime, groupId, fileName } = action.payload
  const apiReq = groupId ? getOrderSummaryByGroupIdApi : getOrderSummaryItemsApi
  // @ts-ignore
  const response: IApiResponse = yield call(apiReq, {
    from_date: startTime,
    to_date: endTime,
    ...(groupId ? { group_id: groupId } : {})
  })

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Something Went Wrong', {
      cause: 'customError'
    })
  }

  let formattedData = []

  if (groupId) {
    formattedData = formatOrderGroupSummaryRowDataDownload(response?.data?.order_summary[0])
  } else {
    formattedData = formatOrderSummaryListRowDataDownload(response?.data?.order_summary)
  }

  const ws = utils.json_to_sheet(formattedData, { origin: 'A15' })
  const wb = utils.book_new()
  const excelHeading = `Order Summary - (${dayjs
    .unix(startTime)
    .format('DD MMM, YYYY')}) to (${dayjs.unix(endTime).format('DD MMM, YYYY')})`

  utils.sheet_add_aoa(ws, [[excelHeading], [], [`Printed On: ${dayjs().format('DD MMM, YYYY')}`]], {
    origin: 'A4'
  })

  if (formattedData.length) {
    ws['!cols'] = getMaxColumnWidths(formattedData)
  }

  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, `${fileName}.xlsx`)
  yield put(
    setOrderSummaryModalsAction({
      modalName: 'orderSummaryDownload',
      status: false
    })
  )
}

export function* getOverallOrderGroupSummaryEffect(
  action: Action<IFetchOrderGroupSummaryActionPayload>
) {
  const response: IApiResponse = yield call(getOverallOrderSummaryGroupApi, {
    from_date: action.payload.startTime,
    to_date: action.payload.endTime,
    page: action.payload.page,
    limit: action.payload.limit
  })
  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    yield put(setLoaderAction({ id: 'overallGroupSummary', value: false }))
    throw new Error('Could not fetch order summary. Please try again after some time.', {
      cause: 'customError'
    })
  }

  const { accontLabels }: ITagsState = yield select((app: IAppState) => app.Tags)
  if (response?.data?.order_items?.length) {
    yield put(
      getOverallOrderGroupSummarySuccessAction({
        data: getFormattedOverallOrderGroupSummaryV2(response.data.order_items, accontLabels),
        page: action.payload.page || 1,
        limit: action.payload.limit || DEFAULT_ORDER_SUMMARY_LIMIT,
        totalItems: response.data.pagination_details?.total_items || 0
      })
    )
  }
  yield put(
    setSummaryActiveFilter({
      startTime: action.payload.startTime,
      endTime: action.payload.endTime,
      filterType: action.payload.filterType
    })
  )
  yield put(setLoaderAction({ id: 'overallGroupSummary', value: false }))
}

export function* downloadOverallGroupSummaryEffect() {
  const {
    activeFilter,
    overallOrderGroupData: { page }
  }: IOrderSummaryState = yield select((app: IAppState) => app.OrderSummary)
  const date = formatDateToString(new Date())
  const defaultDate = {
    startTime: dayjs(date).startOf('day').unix(),
    endTime: dayjs(date).endOf('day').unix()
  }
  const response: IApiResponse = yield call(getOverallOrderSummaryGroupApi, {
    from_date: activeFilter?.startTime || defaultDate.startTime,
    to_date: activeFilter?.endTime || defaultDate.endTime,
    page: page || 1,
    limit: 999999999
  })

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Could not fetch order summary. Please try again after some time.', {
      cause: 'customError'
    })
  }

  const { accontLabels }: ITagsState = yield select((app: IAppState) => app.Tags)
  const data = getFormattedOverallOrderGroupSummaryV2(
    response.data?.order_items || [],
    accontLabels
  )

  const title = 'Order Item Level Summary Data'
  const formattedData = formatDataForExcel(data)
  const ws = utils.json_to_sheet(formattedData, { origin: 'A5' })
  const wb = utils.book_new()
  const excelHeading = getExcelHeading(activeFilter, title)
  utils.sheet_add_aoa(
    ws,
    [
      [excelHeading],
      [],
      [`Summary Data Created On: ${dayjs.unix(dayjs().unix()).format('DD MMM, YYYY')}`],
      []
    ],
    {
      origin: 'A1'
    }
  )

  if (data.length) {
    ws['!cols'] = getOrderColumnWidths(data)
  }

  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, 'overall_summary_data.xlsx')
}
